import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { apiError, can } from "../lib/api";
import { useQuick } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, ChevronRight, Plus, Filter, Copy, ClipboardPaste, X } from "lucide-react";
import { addDays, addWeeks, addMonths, startOfWeek, startOfMonth, endOfMonth, format, isSameDay } from "date-fns";
import { toast } from "sonner";

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7);
const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarPage() {
  const { openModal } = useQuick();
  const { user } = useAuth();
  const [view, setView] = useState("settimana");
  const [current, setCurrent] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [physios, setPhysios] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [medicalBatch, setMedicalBatch] = useState([]);
  const [filters, setFilters] = useState({ physio_id: "", treatment_id: "", status: "", machine_id: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [copiedAppointment, setCopiedAppointment] = useState(() => {
    try {
      const raw = sessionStorage.getItem("ll:copied-appointment");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [contextMenu, setContextMenu] = useState(null);
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);

  const range = useMemo(() => {
    if (view === "giorno") return { from: iso(current), to: iso(current) };
    if (view === "settimana") { const s = startOfWeek(current, { weekStartsOn: 1 }); return { from: iso(s), to: iso(addDays(s, 6)) }; }
    const s = startOfMonth(current);
    return { from: iso(s), to: iso(endOfMonth(current)) };
  }, [view, current]);

  const load = useCallback(async () => {
    const params = { date_from: range.from, date_to: range.to };
    Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
    const [appointmentsResult, medicalResult] = await Promise.allSettled([
      api.get("/appointments", { params }),
      api.get("/medical-availabilities/batch", { params: { date_from: range.from, date_to: range.to } }),
    ]);
    if (appointmentsResult.status === "fulfilled") setAppointments(appointmentsResult.value.data);
    if (medicalResult.status === "fulfilled") setMedicalBatch(medicalResult.value.data.availabilities || []);
    else setMedicalBatch([]);
  }, [range, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/treatments").then(({ data }) => setTreatments(data)).catch(() => {});
    if (can(user, "fisioterapisti", "view") || can(user, "medici", "view")) {
      api.get("/physios").then(({ data }) => setPhysios(data)).catch(() => {});
    }
    if (can(user, "macchinari", "view")) {
      api.get("/machines").then(({ data }) => setMachines(data)).catch(() => {});
    }
    if (can(user, "fisioterapisti", "view")) {
      api.get("/shifts").then(({ data }) => setShifts(data)).catch(() => {});
    }
  }, [user]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ll:data-changed", h);
    return () => window.removeEventListener("ll:data-changed", h);
  }, [load]);

  const nav = (dir) => {
    if (view === "giorno") setCurrent(addDays(current, dir));
    else if (view === "settimana") setCurrent(addWeeks(current, dir));
    else setCurrent(addMonths(current, dir));
  };

  const closeContextMenu = () => setContextMenu(null);

  useEffect(() => {
    const close = () => closeContextMenu();
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, []);

  const showContextMenu = (x, y, kind, payload) => {
    const maxX = Math.max(8, window.innerWidth - 230);
    const maxY = Math.max(8, window.innerHeight - 150);
    setContextMenu({
      x: Math.min(x, maxX),
      y: Math.min(y, maxY),
      kind,
      payload,
    });
  };

  const contextHandlers = (kind, payload) => ({
    onContextMenu: (e) => {
      e.preventDefault();
      e.stopPropagation();
      showContextMenu(e.clientX, e.clientY, kind, payload);
    },
    onClickCapture: (e) => {
      if (!longPressTriggered.current) return;
      e.preventDefault();
      e.stopPropagation();
      longPressTriggered.current = false;
    },
    onTouchStart: (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      longPressTriggered.current = false;
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      longPressTimer.current = setTimeout(() => {
        longPressTriggered.current = true;
        showContextMenu(touch.clientX, touch.clientY, kind, payload);
      }, 550);
    },
    onTouchEnd: () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    },
    onTouchMove: () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      longPressTriggered.current = false;
    },
  });

  const copyAppointment = (appointment) => {
    if (appointment.booking_mode === "medical_slot") {
      toast.error("Gli appuntamenti medici a slot non si copiano: scegli uno slot medico libero");
      closeContextMenu();
      return;
    }
    const professional = professionalMap[appointment.physio_id];
    if (professional?.category === "medico") {
      toast.error("Gli appuntamenti medici si gestiscono tramite gli slot dedicati");
      closeContextMenu();
      return;
    }
    const payload = {
      id: appointment.id,
      patient_name: appointment.patient_name,
      start: appointment.start,
      date: appointment.date,
    };
    sessionStorage.setItem("ll:copied-appointment", JSON.stringify(payload));
    setCopiedAppointment(payload);
    toast.success(`Copiato: ${appointment.patient_name} · ${appointment.start}`);
    closeContextMenu();
  };

  const pasteAppointment = async ({ date, start }) => {
    if (!copiedAppointment) return;
    try {
      await api.post(`/appointments/${copiedAppointment.id}/copy`, {
        date,
        start: start || copiedAppointment.start,
      });
      toast.success("Appuntamento incollato");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Impossibile incollare l'appuntamento"));
    } finally {
      closeContextMenu();
    }
  };

  const byDate = useMemo(() => {
    const m = {};
    appointments.forEach((a) => { (m[a.date] = m[a.date] || []).push(a); });
    return m;
  }, [appointments]);

  const professionalMap = useMemo(() => Object.fromEntries(physios.map((p) => [p.id, p])), [physios]);

  const staffOn = (d) => {
    const wd = (d.getDay() + 6) % 7;
    const ds = iso(d);
    const out = [];
    physios.filter((p) => (p.category || "fisioterapista") !== "medico").forEach((p) => {
      const absent = shifts.some((s) => s.physio_id === p.id && (s.type === "assenza" || s.type === "ferie") && (s.date === ds || s.weekday === wd));
      const sh = shifts.find((s) => s.physio_id === p.id && s.type === "turno" && (s.date === ds || s.weekday === wd));
      if (sh && !absent) out.push({ ...p, shift_start: sh.start, shift_end: sh.end });
    });
    return out;
  };

  const medicalForDate = (date) => medicalBatch.filter((a) => a.date === date && (!filters.physio_id || a.physio_id === filters.physio_id));

  const medicalLanes = (date) => {
    const lanes = new Map();
    medicalForDate(date).forEach((availability) => {
      if (!lanes.has(availability.physio_id)) lanes.set(availability.physio_id, { doctor: availability.doctor, availabilities: [] });
      lanes.get(availability.physio_id).availabilities.push(availability);
    });
    return Array.from(lanes.values()).sort((a, b) => `${a.doctor.last_name} ${a.doctor.first_name}`.localeCompare(`${b.doctor.last_name} ${b.doctor.first_name}`));
  };

  const slotAppointment = (physioId, date, slot) => (byDate[date] || []).find((a) =>
    a.physio_id === physioId && a.status !== "annullato" && a.start < slot.end && a.end > slot.start
  );

  const ApptChip = ({ a, small }) => (
    <button
      key={a.id}
      data-testid={`cal-appt-${a.id}`}
      onClick={(e) => { e.stopPropagation(); openModal("appointment", { appointment: a }); }}
      {...contextHandlers("appointment", { appointment: a })}
      className={`w-full text-left rounded-lg px-2 py-1 border-l-4 transition-all hover:shadow-sm ${a.status === "annullato" ? "opacity-40 line-through" : ""} ${small ? "text-[10px]" : "text-xs"}`}
      style={{ borderColor: a.physio_color, background: `${a.physio_color}14` }}
    >
      <span className="font-semibold">{a.start}</span> <span className="font-medium">{a.patient_name}</span>
      {!small && <span className="block text-slate-500 truncate">{a.treatment_name}{a.machine_name ? ` · ${a.machine_name}` : ""}</span>}
    </button>
  );

  const MedicalDayLanes = ({ date }) => {
    const lanes = medicalLanes(date);
    if (!lanes.length) return null;
    return (
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="medical-day-lanes">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
          <h2 className="font-heading font-bold">Medici / specialisti</h2>
          <p className="text-xs text-slate-500">Slot paralleli prenotabili per medico. Clicca su uno slot libero per prenotare.</p>
        </div>
        <div className="overflow-x-auto">
          <div className="grid gap-3 p-3" style={{ gridTemplateColumns: `repeat(${lanes.length}, minmax(240px, 1fr))`, minWidth: `${Math.max(1, lanes.length) * 240}px` }}>
            {lanes.map(({ doctor, availabilities }) => {
              const total = availabilities.reduce((n, a) => n + a.grid.total, 0);
              const free = availabilities.reduce((n, a) => n + a.grid.free, 0);
              return (
                <div key={doctor.id} className="rounded-2xl border overflow-hidden" style={{ borderColor: `${doctor.color}55`, background: `${doctor.color}08` }} data-testid={`medical-lane-${doctor.id}`}>
                  <div className="px-3 py-3 border-b" style={{ borderColor: `${doctor.color}35`, background: `${doctor.color}15` }}>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: doctor.color }} />
                      <span className="font-semibold text-sm">{doctor.first_name} {doctor.last_name}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{doctor.specialization || "Specialista"}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{free} liberi · {total - free} occupati · {total} totali</p>
                  </div>
                  <div className="p-2 space-y-2">
                    {availabilities.map((availability) => (
                      <div key={availability.id}>
                        <div className="flex items-center justify-between px-1 pb-1">
                          <span className="text-[10px] uppercase tracking-wide text-slate-400">{availability.start}–{availability.end}</span>
                          <span className="text-[10px] text-slate-400">{availability.slot_duration_minutes} min</span>
                        </div>
                        <div className="space-y-1">
                          {availability.grid.slots.map((slot) => {
                            const appt = slotAppointment(doctor.id, date, slot);
                            if (slot.available) {
                              return (
                                <button key={slot.start} data-testid={`medical-slot-${availability.id}-${slot.start}`} onClick={() => openModal("appointment", { medicalSlot: {
                                  availability_id: availability.id,
                                  physio_id: doctor.id,
                                  doctor,
                                  date,
                                  start: slot.start,
                                  end: slot.end,
                                  duration: availability.slot_duration_minutes,
                                } })}
                                  className="w-full flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-left hover:bg-emerald-100 transition-colors">
                                  <span className="font-mono text-xs font-semibold text-slate-700">{slot.start}–{slot.end}</span>
                                  <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">Libero</span>
                                </button>
                              );
                            }
                            return appt ? (
                              <div key={slot.start}><ApptChip a={appt} /></div>
                            ) : (
                              <div key={slot.start} className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-slate-100/70 px-3 py-2 text-xs text-slate-400">
                                <span className="font-mono">{slot.start}–{slot.end}</span><span>Occupato</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const weekDays = useMemo(() => {
    const s = startOfWeek(current, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [current]);

  const monthCells = useMemo(() => {
    const s = startOfWeek(startOfMonth(current), { weekStartsOn: 1 });
    return Array.from({ length: 42 }, (_, i) => addDays(s, i));
  }, [current]);

  const title = view === "giorno"
    ? current.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : view === "settimana"
      ? `${format(weekDays[0], "d MMM")} — ${format(weekDays[6], "d MMM yyyy")}`
      : current.toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-bold capitalize">{title}</h1>
        <div className="flex items-center gap-1 ml-auto">
          <button data-testid="cal-prev" onClick={() => nav(-1)} className="p-2 rounded-xl bg-white border border-slate-200 hover:border-blue-300"><ChevronLeft size={16} /></button>
          <button data-testid="cal-today" onClick={() => setCurrent(new Date())} className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-medium hover:border-blue-300">Oggi</button>
          <button data-testid="cal-next" onClick={() => nav(1)} className="p-2 rounded-xl bg-white border border-slate-200 hover:border-blue-300"><ChevronRight size={16} /></button>
        </div>
        <div className="flex bg-white rounded-full border border-slate-200 p-1" data-testid="cal-view-selector">
          {[["giorno", "Giorno"], ["settimana", "Settimana"], ["mese", "Mese"]].map(([v, l]) => (
            <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${view === v ? "bg-blue-600 text-white" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="cal-filters-toggle" onClick={() => setShowFilters(!showFilters)} className={`p-2 rounded-xl border ${showFilters ? "bg-blue-50 border-blue-300 text-blue-600" : "bg-white border-slate-200"}`}><Filter size={16} /></button>
        <button data-testid="cal-new-appointment" onClick={() => openModal("appointment", { date: iso(current) })} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-700"><Plus size={16} /> Fisioterapia</button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-2 bg-white rounded-2xl border border-slate-100 p-4" data-testid="cal-filters">
          <select data-testid="filter-physio" value={filters.physio_id} onChange={(e) => setFilters({ ...filters, physio_id: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">Tutti i professionisti</option>
            {physios.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.category === "medico" ? " · medico" : ""}</option>)}
          </select>
          <select data-testid="filter-treatment" value={filters.treatment_id} onChange={(e) => setFilters({ ...filters, treatment_id: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">Tutti i trattamenti</option>
            {treatments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select data-testid="filter-machine" value={filters.machine_id} onChange={(e) => setFilters({ ...filters, machine_id: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">Tutti i macchinari</option>
            {machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">Tutti gli stati</option>
            {["prenotato", "confermato", "arrivato", "in_trattamento", "completato", "annullato", "non_presentato"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
          <button data-testid="filter-reset" onClick={() => setFilters({ physio_id: "", treatment_id: "", status: "", machine_id: "" })} className="text-sm text-blue-600 hover:underline px-2">Azzera filtri</button>
        </div>
      )}

      <div className="flex flex-wrap gap-3" data-testid="cal-legend">
        {physios.filter((p) => p.active !== false).map((p) => (
          <span key={p.id} className="flex items-center gap-1.5 text-xs text-slate-500">
            <span className="w-3 h-3 rounded-full" style={{ background: p.color }} />{p.first_name} {p.last_name}
            {p.category === "medico" && <span className="text-[9px] uppercase tracking-wide text-slate-400">medico</span>}
          </span>
        ))}
      </div>

      {copiedAppointment && (
        <div className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2 text-sm" data-testid="calendar-copy-buffer">
          <Copy size={15} className="text-blue-600" />
          <span className="text-slate-600">Copiato:</span>
          <span className="font-semibold">{copiedAppointment.patient_name}</span>
          <span className="text-slate-500">{copiedAppointment.start}</span>
          <button type="button" onClick={() => { sessionStorage.removeItem("ll:copied-appointment"); setCopiedAppointment(null); }} className="ml-auto p-1 rounded-lg hover:bg-white text-slate-500" title="Svuota copia"><X size={14} /></button>
        </div>
      )}

      {view === "giorno" && (
        <>
          <MedicalDayLanes date={iso(current)} />

          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="cal-day-view">
            <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60" data-testid="cal-day-staff">
              <div className="w-full"><h2 className="font-heading font-bold">Fisioterapia</h2><p className="text-xs text-slate-500">Agenda flessibile dei fisioterapisti</p></div>
              {staffOn(current).map((p) => (
                <span key={p.id} data-testid={`cal-day-staff-${p.id}`} className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border" style={{ background: `${p.color}14`, color: p.color, borderColor: `${p.color}40` }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                  {p.first_name} {p.last_name} · {p.shift_start}–{p.shift_end}
                </span>
              ))}
              {staffOn(current).length === 0 && <p className="text-xs text-slate-400">Nessun fisioterapista in turno in questa data</p>}
            </div>
            {HOURS.map((h) => {
              const slot = `${String(h).padStart(2, "0")}:00`;
              const dayAppts = (byDate[iso(current)] || []).filter((a) => {
                const p = professionalMap[a.physio_id];
                return (p?.category || "fisioterapista") !== "medico" && parseInt(a.start.split(":")[0], 10) === h;
              });
              return (
                <div key={h} className="flex border-b border-slate-50 min-h-[56px] group">
                  <button data-testid={`cal-slot-${slot}`} onClick={() => openModal("appointment", { date: iso(current), start: slot })} {...contextHandlers("slot", { date: iso(current), start: slot })} className="w-16 shrink-0 text-xs text-slate-400 font-mono py-2 border-r border-slate-50 group-hover:bg-blue-50/50 transition-colors">{slot}</button>
                  <div className="flex-1 p-1.5 space-y-1 cursor-pointer" onClick={() => dayAppts.length === 0 && openModal("appointment", { date: iso(current), start: slot })} {...contextHandlers("slot", { date: iso(current), start: slot })}>
                    {dayAppts.map((a) => <ApptChip key={a.id} a={a} />)}
                  </div>
                </div>
              );
            })}
          </div>

          {(() => {
            const day = iso(current);
            const avs = medicalForDate(day);
            const legacy = (byDate[day] || []).filter((a) => {
              const p = professionalMap[a.physio_id];
              if (p?.category !== "medico" || a.booking_mode === "medical_slot") return false;
              return !avs.some((v) => v.physio_id === a.physio_id && a.start < v.end && a.end > v.start);
            });
            if (!legacy.length) return null;
            return (
              <div className="bg-white rounded-2xl border border-amber-100 p-4">
                <h2 className="font-heading font-bold">Appuntamenti medici precedenti al sistema slot</h2>
                <p className="text-xs text-slate-500 mb-2">Restano visibili e validi senza migrazione.</p>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-2">{legacy.map((a) => <ApptChip key={a.id} a={a} />)}</div>
              </div>
            );
          })()}
        </>
      )}

      {view === "settimana" && (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto" data-testid="cal-week-view">
          <div className="grid grid-cols-7 min-w-[980px]">
            {weekDays.map((d, i) => {
              const ds = iso(d);
              const lanes = medicalLanes(ds);
              return (
                <div key={i} className={`border-r border-slate-50 last:border-r-0 ${isSameDay(d, new Date()) ? "bg-blue-50/40" : ""}`}>
                  <button data-testid={`cal-weekday-${ds}`} onClick={() => { setCurrent(d); setView("giorno"); }} className="w-full text-center py-3 border-b border-slate-100 hover:bg-slate-50">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400">{DAY_NAMES[i]}</p>
                    <p className={`font-heading font-bold ${isSameDay(d, new Date()) ? "text-blue-600" : "text-slate-800"}`}>{d.getDate()}</p>
                  </button>
                  {lanes.length > 0 && (
                    <div className="px-1.5 pt-1.5 space-y-1" data-testid={`cal-medical-week-${ds}`}>
                      {lanes.map(({ doctor, availabilities }) => {
                        const total = availabilities.reduce((n, a) => n + a.grid.total, 0);
                        const free = availabilities.reduce((n, a) => n + a.grid.free, 0);
                        return <button key={doctor.id} onClick={() => { setCurrent(d); setView("giorno"); }} className="w-full text-[9px] font-semibold px-1.5 py-1 rounded-lg text-left" style={{ background: `${doctor.color}1A`, color: doctor.color }} title={`${doctor.first_name} ${doctor.last_name}: ${free}/${total} slot liberi`}>{doctor.first_name} · {free}/{total} liberi</button>;
                      })}
                    </div>
                  )}
                  <div className="px-1.5 pt-1.5 flex flex-wrap gap-1" data-testid={`cal-weekstaff-${ds}`}>
                    {staffOn(d).map((p) => <span key={p.id} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full truncate" style={{ background: `${p.color}1A`, color: p.color }} title={`${p.first_name} ${p.last_name} ${p.shift_start}–${p.shift_end}`}>{p.first_name}</span>)}
                  </div>
                  <div className="p-1.5 space-y-1 min-h-[180px] cursor-pointer" onClick={() => openModal("appointment", { date: ds })} {...contextHandlers("slot", { date: ds, start: copiedAppointment?.start })}>
                    {(byDate[ds] || []).map((a) => <ApptChip key={a.id} a={a} small />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "mese" && (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="cal-month-view">
          <div className="grid grid-cols-7 border-b border-slate-100">
            {DAY_NAMES.map((d) => <p key={d} className="text-center text-[10px] uppercase tracking-widest text-slate-400 py-2">{d}</p>)}
          </div>
          <div className="grid grid-cols-7">
            {monthCells.map((d, i) => {
              const ds = iso(d);
              const dayAppts = byDate[ds] || [];
              const lanes = medicalLanes(ds);
              const inMonth = d.getMonth() === current.getMonth();
              return (
                <button key={i} data-testid={`cal-monthday-${ds}`} onClick={() => { setCurrent(d); setView("giorno"); }} {...contextHandlers("slot", { date: ds, start: copiedAppointment?.start })} className={`min-h-[96px] border-b border-r border-slate-50 p-1.5 text-left align-top hover:bg-blue-50/40 transition-colors ${!inMonth ? "opacity-30" : ""} ${isSameDay(d, new Date()) ? "bg-blue-50/60" : ""}`}>
                  <p className={`text-xs font-semibold ${isSameDay(d, new Date()) ? "text-blue-600" : "text-slate-600"}`}>{d.getDate()}</p>
                  <div className="space-y-0.5 mt-0.5" data-testid={`cal-monthstaff-${ds}`}>
                    {lanes.slice(0, 2).map(({ doctor, availabilities }) => {
                      const free = availabilities.reduce((n, a) => n + a.grid.free, 0);
                      const total = availabilities.reduce((n, a) => n + a.grid.total, 0);
                      return <span key={doctor.id} className="block text-[8px] font-bold px-1 py-px rounded truncate" style={{ background: `${doctor.color}1A`, color: doctor.color }}>{doctor.first_name} {doctor.last_name?.[0]}. · {free}/{total}</span>;
                    })}
                  </div>
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {dayAppts.slice(0, 6).map((a) => <span key={a.id} className="w-2 h-2 rounded-full" style={{ background: a.physio_color }} title={`${a.start} ${a.patient_name}`} />)}
                  </div>
                  {dayAppts.length > 0 && <p className="text-[10px] text-slate-400 mt-0.5">{dayAppts.length} app.</p>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {contextMenu && (
        <div
          className="fixed z-[100] min-w-[210px] rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
          data-testid="calendar-context-menu"
        >
          {contextMenu.kind === "appointment" && (
            <button
              type="button"
              onClick={() => copyAppointment(contextMenu.payload.appointment)}
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-left hover:bg-slate-50"
            >
              <Copy size={15} /> Copia appuntamento
            </button>
          )}
          {contextMenu.kind === "slot" && copiedAppointment && (
            <button
              type="button"
              onClick={() => pasteAppointment(contextMenu.payload)}
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-left hover:bg-blue-50 text-blue-700"
            >
              <ClipboardPaste size={15} /> Incolla qui
            </button>
          )}
          {contextMenu.kind === "slot" && !copiedAppointment && (
            <div className="px-3 py-2 text-xs text-slate-400">Prima copia un appuntamento</div>
          )}
        </div>
      )}
    </div>
  );
}
